import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ReferralService} from '../../../shared/services/referral.service';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {RefPromptService} from '../shared/ref-prompt.component';
import {PER_PAGE} from '../shared/ref-utils';

type RedemptionStatus = 'REQUESTED' | 'UNDER_REVIEW' | 'APPROVED' | 'PROCESSING' | 'PAID' | 'REJECTED' | 'FAILED' | 'CANCELLED';

interface PayoutRow {
    internalId: number;
    reference: string;
    referrerName: string;
    referrerCode: string;
    referrerMobile: string;
    points: number;
    amount: number;
    status: RedemptionStatus;
    payoutType: string;
    destination: string;
    bankName: string;
    requestedAt: string;
    transactionReference: string;
    rejectionReason: string;
    failureReason: string;
}

const STATUS_LABEL: { [k in RedemptionStatus]: string } = {
    REQUESTED: 'Requested', UNDER_REVIEW: 'Under review', APPROVED: 'Approved', PROCESSING: 'Processing',
    PAID: 'Paid', REJECTED: 'Rejected', FAILED: 'Failed', CANCELLED: 'Cancelled'
};

// CSS pill classes defined in referral-shell.component.scss (p-active/p-verified = green,
// p-pending/p-enrolled = amber, p-rejected/p-blocked = red, p-new/p-none = neutral).
const STATUS_CLASS: { [k in RedemptionStatus]: string } = {
    REQUESTED: 'pending', UNDER_REVIEW: 'pending', APPROVED: 'verified', PROCESSING: 'pending',
    PAID: 'verified', REJECTED: 'rejected', FAILED: 'rejected', CANCELLED: 'none'
};

@Component({
    selector: 'app-referral-payouts',
    templateUrl: './referral-payouts.component.html'
})
export class ReferralPayoutsComponent implements OnInit, OnDestroy {
    // Buckets, not single statuses — matches the backend's comma-separated `status` filter
    // support added for this page (RedemptionService::applyAdminFilters).
    status = 'REQUESTED,UNDER_REVIEW';
    page = 0;
    per = PER_PAGE;
    total = 0;
    loading = false;
    list: PayoutRow[] = [];
    kpis: { label: string, value: string, sub: string }[] = [];

    STATUS_LABEL = STATUS_LABEL;
    STATUS_CLASS = STATUS_CLASS;
    private subs: Subscription[] = [];

    constructor(public referralService: ReferralService,
                private referralApi: ReferralApiService,
                private route: ActivatedRoute,
                private prompt: RefPromptService) {
    }

    ngOnInit() {
        this.subs.push(this.route.queryParamMap.subscribe(qp => {
            if (qp.has('status')) {
                this.status = qp.get('status');
            }
            this.onFilter();
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }

    get pageRows(): PayoutRow[] {
        return this.list;
    }

    onFilter() {
        this.page = 0;
        this.load();
        this.loadSummary();
    }

    setPage(n: number) {
        this.page = n;
        this.load();
    }

    load() {
        this.loading = true;
        const params: any = {page: this.page + 1, per_page: this.per};
        if (this.status) {
            params.status = this.status;
        }

        this.referralApi.getRedemptions(params).subscribe({
            next: (data: any) => {
                this.total = data?.total ?? 0;
                this.list = (data?.data ?? []).map((r: any) => this.toRow(r));
                this.loading = false;
            },
            error: () => {
                this.list = [];
                this.total = 0;
                this.loading = false;
            }
        });
    }

    loadSummary() {
        this.referralApi.getRedemptionsSummary().subscribe({
            next: (s: any) => {
                this.kpis = [
                    {label: 'Waiting for approval', value: this.referralService.inr(s?.waiting_approval?.amount ?? 0), sub: (s?.waiting_approval?.count ?? 0) + ' payouts'},
                    {label: 'Approved, not paid', value: this.referralService.inr(s?.approved_not_paid?.amount ?? 0), sub: (s?.approved_not_paid?.count ?? 0) + ' payouts'},
                    {label: 'Paid, all time', value: this.referralService.inr(s?.paid_all_time?.amount ?? 0), sub: (s?.paid_all_time?.count ?? 0) + ' payouts'},
                    {label: 'Rejected / failed', value: this.referralService.inr(s?.rejected_or_failed?.amount ?? 0), sub: (s?.rejected_or_failed?.count ?? 0) + ' payouts'}
                ];
            },
            error: () => {
                this.kpis = [];
            }
        });
    }

    private toRow(r: any): PayoutRow {
        const payout = r.payout_method ?? {};
        return {
            internalId: r.internal_id,
            reference: r.id,
            referrerName: r.referrer?.name ?? '—',
            referrerCode: r.referrer?.referral_code ?? '',
            referrerMobile: r.referrer?.mobile ?? '',
            points: r.points,
            amount: r.amount,
            status: r.status,
            payoutType: payout.payout_type ?? '',
            destination: payout.destination ?? '',
            bankName: payout.bank_name ?? '',
            requestedAt: r.requested_at,
            transactionReference: r.transaction_reference,
            rejectionReason: r.rejection_reason,
            failureReason: r.failure_reason
        };
    }

    fmt(iso: string): string {
        return iso ? new Date(iso).toLocaleDateString('en-IN', {day: 'numeric', month: 'short', year: 'numeric'}) : '—';
    }

    async approve(p: PayoutRow) {
        this.referralApi.approveRedemption(p.internalId).subscribe(() => {
            this.referralService.toast(`Approved payout to ${p.referrerName}`);
            this.load();
            this.loadSummary();
        });
    }

    async reject(p: PayoutRow) {
        const reason = await this.prompt.ask({
            title: `Reject payout to ${p.referrerName}?`,
            text: 'The reserved points are returned to their wallet. They will see this reason.',
            label: 'Reason',
            okText: 'Reject payout',
            check: v => v.trim().length >= 3 ? '' : 'Enter at least 3 characters.'
        });
        if (reason !== null) {
            this.referralApi.rejectRedemption(p.internalId, {reason}).subscribe(() => {
                this.referralService.toast('Payout rejected');
                this.load();
                this.loadSummary();
            });
        }
    }

    async process(p: PayoutRow) {
        this.referralApi.processRedemption(p.internalId).subscribe(() => {
            this.referralService.toast(`Moved to processing for ${p.referrerName}`);
            this.load();
            this.loadSummary();
        });
    }

    async markPaid(p: PayoutRow) {
        const txn = await this.prompt.ask({
            title: `Mark ${this.referralService.inr(p.amount)} as paid to ${p.referrerName}?`,
            text: `Pay to ${p.destination || p.payoutType}. The partner will see the transaction reference.`,
            label: 'UPI or bank transaction reference',
            okText: 'Mark as paid',
            check: v => v.trim().length >= 3 ? '' : 'Enter at least 3 characters.'
        });
        if (txn !== null) {
            this.referralApi.markRedemptionPaid(p.internalId, {transaction_reference: txn}).subscribe(() => {
                this.referralService.toast('Marked as paid');
                this.load();
                this.loadSummary();
            });
        }
    }

    async markFailed(p: PayoutRow) {
        const reason = await this.prompt.ask({
            title: `Mark payout to ${p.referrerName} as failed?`,
            text: 'Use this when the bank/UPI transfer itself failed. The reserved points are returned to their wallet.',
            label: 'Reason',
            okText: 'Mark as failed',
            check: v => v.trim().length >= 3 ? '' : 'Enter at least 3 characters.'
        });
        if (reason !== null) {
            this.referralApi.markRedemptionFailed(p.internalId, {reason}).subscribe(() => {
                this.referralService.toast('Payout marked as failed');
                this.load();
                this.loadSummary();
            });
        }
    }

    exportForBank() {
        const rows = this.list.filter(p => p.status === 'APPROVED' || p.status === 'PROCESSING');
        if (!rows.length) {
            this.referralService.toast('No approved payouts on this page to export');
            return;
        }
        this.referralService.csv('approved-payouts.csv', ([['Partner', 'Code', 'Payout type', 'Destination', 'Bank', 'Amount', 'Reference']] as any[][]).concat(
            rows.map(p => [p.referrerName, p.referrerCode, p.payoutType, p.destination, p.bankName, p.amount, p.reference])));
    }
}
